import type { IContentImageRef } from './media-contracts';
import type { ContentPostStatus, IContentAccount, IContentPostVersion } from './post-contracts';

/**
 * The website's team page as survey-api answers it — `/api/v1/admin/website/team`
 * (its design, survey-api's `docs/superpowers/specs/2026-10-07-website-content-design.md` §4b).
 *
 * **A transcription, like `post-contracts.ts` beside it**, and it borrows three types from there
 * rather than restating them: a person and a post share a status vocabulary, an account reference
 * and the shape of a history entry, because they are the same store answering about two kinds of
 * record. What differs is every field that carries meaning, which is why they are two files.
 *
 * Two things the wire decides that the mock had guessed differently:
 *
 * - **A role is `roleTitle`, not `title`.** Beside a `name`, a field called `title` reads as a
 *   courtesy title, and a post's `title` is a third thing again.
 * - **The headshot is a library id on the way in and a resolved image on the way out**, as a
 *   post's lead image is. WEB-07 AC2 — "text and photograph change independently" — is answered
 *   by both travelling in a person's content: either can be saved without the other, and a revert
 *   brings back the picture that was theirs at the time.
 */

export interface IContentTeamMember {
  memberId: string;
  name: string;
  /** Their role, as the page prints it. */
  roleTitle: string;
  /** The team page's filters they sit under, in `TEAM_GROUPS` order; empty is "All" only. */
  groups: ContentTeamGroup[];
  /** HTML, as survey-api's sanitiser kept it — never what this app last sent. */
  bio: string;
  /** Their photograph, resolved — null where none is set. */
  photo: Nullable<IContentImageRef>;
  /**
   * Where they stand on the page: 1-based and contiguous across everybody not archived, and `0`
   * for those who are. The list arrives in this order, so a screen draws it as sent rather than
   * sorting a copy of its own.
   */
  position: number;
  status: ContentPostStatus;
  /** When they first appeared on the page; kept through an archive. */
  publishedAt: Nullable<string>;
  /** The `If-Match` value. Held here as well as in the `ETag`, as a post's is. */
  revision: number;
  createdAt: string;
  createdBy: Nullable<IContentAccount>;
  updatedAt: string;
  updatedBy: Nullable<IContentAccount>;
  /** Oldest first. A reorder writes none of these — moving somebody is not an edit to them. */
  history: IContentPostVersion[];
}

/** A person's editable half, as a create and a save send it. No position: the arrangement is set
 *  by its own route and never by editing somebody. */
export interface IContentTeamMemberBody {
  name: string;
  roleTitle: string;
  groups: ContentTeamGroup[];
  bio: string;
  /** Their photograph, as a library id, or null (WEB-09 AC1). */
  photoImageId: Nullable<string>;
}

/**
 * The team page's filters, survey-api's `ContentTeamGroups`: a fixed list, since the site draws
 * one tab per code. The order is the site's tab order and the one survey-api stores a set in.
 */
export const TEAM_GROUPS = [
  { code: 'leadership', label: 'Leadership' },
  { code: 'research-operations', label: 'Research & Operations' },
  { code: 'marketing-events', label: 'Marketing & Events' },
  { code: 'relationship-management', label: 'Relationship Management' }
] as const;

export type ContentTeamGroup = (typeof TEAM_GROUPS)[number]['code'];
